/**
 * QZ Tray Integration Hook
 * 
 * Professionelle Drucklösung für Cash-X POS
 * Ersetzt den benutzerdefinierten Print-Agent durch QZ Tray
 * 
 * Features:
 * - WebSocket-Verbindung zu QZ Tray Desktop App
 * - Silent Printing (ohne Dialoge) mit Zertifikaten
 * - Unterstützung für 80mm Thermodrucker und A4
 * - PDF und HTML Druck
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// QZ Tray dynamisch laden
let qz = null;

export const useQZTray = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [error, setError] = useState(null);
  const [qzLoaded, setQzLoaded] = useState(false);
  const connectionAttemptRef = useRef(0);

  // QZ Tray Library laden
  useEffect(() => {
    const loadQZ = async () => {
      try {
        if (typeof window !== 'undefined') {
          // Dynamischer Import für QZ Tray
          const qzModule = await import('qz-tray');
          qz = qzModule.default || qzModule;
          setQzLoaded(true);
          console.log('[QZ Tray] Library geladen');
        }
      } catch (err) {
        console.error('[QZ Tray] Fehler beim Laden der Library:', err);
        setError('QZ Tray Library konnte nicht geladen werden');
      }
    };
    loadQZ();
  }, []);

  // Zertifikat-Setup für Silent Printing
  // HINWEIS: Ohne gültiges QZ Industries Zertifikat wird ein Bestätigungsdialog angezeigt
  const setupSecurity = useCallback(() => {
    if (!qz) return;

    // Für Entwicklung: Keine Zertifikate setzen
    // Das zeigt den normalen "Allow/Block" Dialog mit aktivem Allow-Button
    // Der User kann dann "Remember this decision" ankreuzen
    
    // Zertifikat auf null setzen (kein Zertifikat = normaler Dialog)
    qz.security.setCertificatePromise((resolve, reject) => {
      resolve(null);
    });

    // Signatur auf null setzen
    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        resolve(null);
      };
    });
  }, []);

  // Verbindung zu QZ Tray herstellen
  const connect = useCallback(async () => {
    if (!qz || isConnecting || isConnected) return false;

    setIsConnecting(true);
    setError(null);
    connectionAttemptRef.current++;
    const currentAttempt = connectionAttemptRef.current;

    try {
      // Security Setup vor Verbindung
      setupSecurity();

      // Verbindung mit Retry-Logik
      await qz.websocket.connect({
        retries: 3,
        delay: 1
      });

      // Nur fortfahren wenn dies der aktuelle Verbindungsversuch ist
      if (currentAttempt !== connectionAttemptRef.current) return false;

      setIsConnected(true);
      console.log('[QZ Tray] Verbunden!');

      // Drucker abrufen
      const printerList = await qz.printers.find();
      setPrinters(printerList || []);
      console.log('[QZ Tray] Drucker gefunden:', printerList?.length || 0);

      // Standarddrucker setzen
      if (printerList && printerList.length > 0) {
        // Versuche den zuletzt verwendeten Drucker zu laden
        const savedPrinter = localStorage.getItem('qz-tray-default-printer');
        if (savedPrinter && printerList.includes(savedPrinter)) {
          setSelectedPrinter(savedPrinter);
        } else {
          setSelectedPrinter(printerList[0]);
        }
      }

      return true;
    } catch (err) {
      if (currentAttempt !== connectionAttemptRef.current) return false;

      console.error('[QZ Tray] Verbindungsfehler:', err.message);
      setError(getErrorMessage(err));
      setIsConnected(false);
      return false;
    } finally {
      if (currentAttempt === connectionAttemptRef.current) {
        setIsConnecting(false);
      }
    }
  }, [isConnecting, isConnected, setupSecurity]);

  // Verbindung trennen
  const disconnect = useCallback(async () => {
    if (!qz || !isConnected) return;

    try {
      await qz.websocket.disconnect();
      setIsConnected(false);
      setPrinters([]);
      console.log('[QZ Tray] Verbindung getrennt');
    } catch (err) {
      console.error('[QZ Tray] Fehler beim Trennen:', err.message);
    }
  }, [isConnected]);

  // Drucker auswählen und speichern
  const selectPrinter = useCallback((printerName) => {
    setSelectedPrinter(printerName);
    localStorage.setItem('qz-tray-default-printer', printerName);
  }, []);

  // Druckerliste aktualisieren
  const refreshPrinters = useCallback(async () => {
    if (!qz || !isConnected) return [];

    try {
      const printerList = await qz.printers.find();
      setPrinters(printerList || []);
      return printerList || [];
    } catch (err) {
      console.error('[QZ Tray] Fehler beim Aktualisieren der Drucker:', err.message);
      return [];
    }
  }, [isConnected]);

  // HTML als PDF drucken (für 80mm Thermodrucker)
  const printThermalReceipt = useCallback(async (htmlContent, printerName = null) => {
    if (!qz || !isConnected) {
      throw new Error('QZ Tray nicht verbunden');
    }

    const printer = printerName || selectedPrinter;
    if (!printer) {
      throw new Error('Kein Drucker ausgewählt');
    }

    try {
      // Konfiguration für 80mm Thermodrucker
      const config = qz.configs.create(printer, {
        units: 'mm',
        size: { width: 72, height: null }, // Auto-Höhe
        margins: { top: 0, right: 0, bottom: 0, left: 0 },
        colorType: 'blackwhite',
        interpolation: 'nearest-neighbor',
        scaleContent: true
      });

      const data = [{
        type: 'pixel',
        format: 'html',
        flavor: 'plain',
        data: htmlContent,
        options: {
          pageWidth: 72,
          pageHeight: null
        }
      }];

      await qz.print(config, data);
      console.log('[QZ Tray] Thermodruck erfolgreich');
      return true;
    } catch (err) {
      console.error('[QZ Tray] Druckfehler:', err.message);
      throw err;
    }
  }, [isConnected, selectedPrinter]);

  // PDF drucken (Base64)
  const printPDF = useCallback(async (base64Data, options = {}, printerName = null) => {
    if (!qz || !isConnected) {
      throw new Error('QZ Tray nicht verbunden');
    }

    const printer = printerName || selectedPrinter;
    if (!printer) {
      throw new Error('Kein Drucker ausgewählt');
    }

    try {
      const config = qz.configs.create(printer, {
        units: options.units || 'mm',
        size: options.size || { width: 72, height: null },
        margins: options.margins || 0,
        colorType: options.colorType || 'blackwhite',
        scaleContent: true
      });

      const data = [{
        type: 'pixel',
        format: 'pdf',
        flavor: 'base64',
        data: base64Data
      }];

      await qz.print(config, data);
      console.log('[QZ Tray] PDF-Druck erfolgreich');
      return true;
    } catch (err) {
      console.error('[QZ Tray] PDF-Druckfehler:', err.message);
      throw err;
    }
  }, [isConnected, selectedPrinter]);

  // A4 Druck
  const printA4 = useCallback(async (htmlContent, printerName = null) => {
    if (!qz || !isConnected) {
      throw new Error('QZ Tray nicht verbunden');
    }

    const printer = printerName || selectedPrinter;
    if (!printer) {
      throw new Error('Kein Drucker ausgewählt');
    }

    try {
      const config = qz.configs.create(printer, {
        units: 'mm',
        size: { width: 210, height: 297 }, // A4
        margins: { top: 10, right: 10, bottom: 10, left: 10 },
        scaleContent: true
      });

      const data = [{
        type: 'pixel',
        format: 'html',
        flavor: 'plain',
        data: htmlContent
      }];

      await qz.print(config, data);
      console.log('[QZ Tray] A4-Druck erfolgreich');
      return true;
    } catch (err) {
      console.error('[QZ Tray] A4-Druckfehler:', err.message);
      throw err;
    }
  }, [isConnected, selectedPrinter]);

  // Raw ESC/POS Befehle senden
  const printRaw = useCallback(async (commands, printerName = null) => {
    if (!qz || !isConnected) {
      throw new Error('QZ Tray nicht verbunden');
    }

    const printer = printerName || selectedPrinter;
    if (!printer) {
      throw new Error('Kein Drucker ausgewählt');
    }

    try {
      const config = qz.configs.create(printer);
      const data = Array.isArray(commands) ? commands : [commands];

      await qz.print(config, data);
      console.log('[QZ Tray] Raw-Druck erfolgreich');
      return true;
    } catch (err) {
      console.error('[QZ Tray] Raw-Druckfehler:', err.message);
      throw err;
    }
  }, [isConnected, selectedPrinter]);

  // Testdruck senden
  const sendTestPrint = useCallback(async (printerName = null) => {
    const printer = printerName || selectedPrinter;
    if (!printer) {
      throw new Error('Kein Drucker ausgewählt');
    }

    const testHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            width: 72mm;
            margin: 0;
            padding: 3mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .line { border-top: 1px dashed #000; margin: 3mm 0; }
        </style>
      </head>
      <body>
        <div class="center bold" style="font-size: 16px;">CASH-X</div>
        <div class="center" style="font-size: 14px;">QZ Tray Testdruck</div>
        <div class="line"></div>
        <div class="center">✅ Verbindung erfolgreich!</div>
        <div class="line"></div>
        <div>Drucker: ${printer}</div>
        <div>Zeit: ${new Date().toLocaleString('de-DE')}</div>
        <div class="line"></div>
        <div class="center bold">Drucksystem bereit</div>
        <div class="line"></div>
      </body>
      </html>
    `;

    return printThermalReceipt(testHTML, printer);
  }, [selectedPrinter, printThermalReceipt]);

  return {
    // Status
    isConnected,
    isConnecting,
    qzLoaded,
    error,
    
    // Drucker
    printers,
    selectedPrinter,
    selectPrinter,
    refreshPrinters,
    
    // Aktionen
    connect,
    disconnect,
    
    // Druckfunktionen
    printThermalReceipt,
    printPDF,
    printA4,
    printRaw,
    sendTestPrint
  };
};

// Hilfsfunktion für benutzerfreundliche Fehlermeldungen
function getErrorMessage(err) {
  const message = err?.message || String(err);
  
  if (message.includes('Unable to connect') || message.includes('WebSocket')) {
    return 'QZ Tray ist nicht gestartet. Bitte starten Sie die QZ Tray Anwendung auf Ihrem Computer.';
  }
  
  if (message.includes('printer')) {
    return 'Drucker nicht gefunden. Stellen Sie sicher, dass der Drucker eingeschaltet und verbunden ist.';
  }
  
  if (message.includes('signature') || message.includes('certificate')) {
    return 'Zertifikatsfehler. Silent Printing ist möglicherweise nicht verfügbar.';
  }
  
  return message;
}

export default useQZTray;
